/** @file Key selector for moveKeys/setCurve/deleteKeys: channel + target to keys and a writer. */
import { z } from 'zod';
import type { Draft } from 'immer';
import type { AnimationDoc, Curve, DeformKey, Doc, Id } from '@pose2d/format/types';
import { throwError } from '@pose2d/format/throwError';
import type { CommandContext } from '../commandContext';
import type { KeyChannel } from '../_shared/defineKeyCommand';
import { KEY_CHANNELS, type AnyKey } from '../_shared/keyChannels';
import { keyGroup } from '../_shared/keyGroup';
import { animationDraft } from './animationDraft';

/** A KEY_CHANNELS channel, or one of the three timelines with their own key shape. */
export type TimelineChannel = KeyChannel | 'deform' | 'drawOrder' | 'event';
/** The part every key shares: its frame, and a curve on numeric timelines. */
export type FramedKey = { f: number; curve?: Curve | undefined };
/** One selected timeline of one animation. */
export type TimelineRef = {
  /** The selected keys in frame order (event keys filtered to the `target` event when one is given). */
  keys: FramedKey[];
  /** True when curves apply (numeric key channels and deform). */
  numeric: boolean;
  /**
   * The lane of a key: keys of one lane replace each other on a frame. Events stack one lane per event (one key per
   * event and frame, as animation.setEventKey keeps it); every other timeline is a single lane.
   */
  lane(key: FramedKey): string;
  /** Document path of the timeline, for warnings. */
  path: string;
  /** Stores `next` (a new array) in place of `keys`; an empty list prunes the timeline. */
  write(next: FramedKey[]): void;
};
type Selector = { animation: string; channel: TimelineChannel; target?: string | undefined };
type Anim = Draft<AnimationDoc>;
type Special = 'deform' | 'drawOrder' | 'event';
type Handler = (anim: Anim, animId: Id, sel: Selector, ctx: CommandContext) => TimelineRef;

const CHANNELS = [...Object.keys(KEY_CHANNELS), 'deform', 'drawOrder', 'event'] as TimelineChannel[];

function requireTarget(sel: Selector): string {
  return sel.target ?? throwError('VALIDATION', undefined, { path: 'target', hint: `${sel.channel} keys need a target` });
}

function channelRef(draft: Draft<Doc>, animId: Id, channel: KeyChannel, target: string, ctx: CommandContext): TimelineRef {
  const row = KEY_CHANNELS[channel];
  const group = keyGroup(draft, animId, row.group);
  const id = ctx.resolve(row.kind, target);
  return {
    keys: group[id]?.[row.prop] ?? [],
    numeric: row.numeric,
    lane: () => '',
    path: `animations.${animId}.${row.group}.${id}.${row.prop}`,
    write(next) {
      const track = (group[id] ??= {});
      if (next.length > 0) track[row.prop] = next as AnyKey[];
      else Reflect.deleteProperty(track, row.prop);
      if (Object.keys(track).length === 0) Reflect.deleteProperty(group, id);
    },
  };
}

function deformRef(anim: Anim, animId: Id, target: string, ctx: CommandContext): TimelineRef {
  const id = ctx.resolve('attachment', target);
  return {
    keys: anim.deform[id] ?? [],
    numeric: true,
    lane: () => '',
    path: `animations.${animId}.deform.${id}`,
    write(next) {
      if (next.length > 0) anim.deform[id] = next as DeformKey[];
      else Reflect.deleteProperty(anim.deform, id);
    },
  };
}

function drawOrderRef(anim: Anim, animId: Id, target: string | undefined): TimelineRef {
  if (target !== undefined) throwError('VALIDATION', undefined, { path: 'target', hint: 'drawOrder keys have no target' });
  return {
    keys: anim.drawOrder,
    numeric: false,
    lane: () => '',
    path: `animations.${animId}.drawOrder`,
    write(next) {
      anim.drawOrder = next as Anim['drawOrder'];
    },
  };
}

function eventRef(anim: Anim, animId: Id, target: string | undefined, ctx: CommandContext): TimelineRef {
  const only = target === undefined ? null : ctx.resolve('event', target);
  const keys: FramedKey[] = anim.events.filter((e) => only === null || e.event === only);
  const frameOf = new Map(keys.map((k): [FramedKey, number] => [k, k.f]));
  return {
    keys,
    numeric: false,
    lane: (k) => (k as Anim['events'][number]).event,
    path: `animations.${animId}.events`,
    write(next) {
      // Unpicked events, and picked keys that survive on their own frame, keep their place; moved keys go after the
      // keys already on their new frame (stable sort). A filtered edit never reorders the other same-frame events.
      const kept = new Set(next);
      const stays = (e: FramedKey): boolean => !frameOf.has(e) || (kept.has(e) && frameOf.get(e) === e.f);
      const moved = next.filter((k) => !stays(k));
      anim.events = [...anim.events.filter(stays), ...(moved as Anim['events'])].sort((a, b) => a.f - b.f);
    },
  };
}

/** The three timelines with their own key shape; every other channel is a KEY_CHANNELS channel. */
const SPECIAL: Record<Special, Handler> = {
  deform: (anim, animId, sel, ctx) => deformRef(anim, animId, requireTarget(sel), ctx),
  drawOrder: (anim, animId, sel) => drawOrderRef(anim, animId, sel.target),
  event: (anim, animId, sel, ctx) => eventRef(anim, animId, sel.target, ctx),
};

function isSpecial(channel: TimelineChannel): channel is Special {
  return Object.hasOwn(SPECIAL, channel);
}

/** Channel schema and resolver for inputs that name any timeline of one animation. */
export const timelineKeys = {
  /** Every selectable channel: the KEY_CHANNELS names plus deform, drawOrder and event. */
  channel: z.enum(CHANNELS),
  /** Resolves animation, channel and target; VALIDATION at `target` when it is missing or not allowed. */
  resolve(draft: Draft<Doc>, sel: Selector, ctx: CommandContext): TimelineRef {
    const { id: animId, anim } = animationDraft(draft, sel.animation, ctx);
    if (isSpecial(sel.channel)) return SPECIAL[sel.channel](anim, animId, sel, ctx);
    return channelRef(draft, animId, sel.channel, requireTarget(sel), ctx);
  },
};
